import { Modal } from "ll-common-overlay";
import { announceStatus, cachedFetch, createMediaImg, deepQuerySelector, matchesMediaQuery } from "ll-common-utilities";
import { Delegate, PhotoSwipeLightbox, animate, inView, scroll, stagger } from "vendor";
/* ─── gift-card-recipient ───────────────────────────────────────────── */
var GiftCardRecipient = class extends HTMLElement {
  #offsetProperty;
  #recipientCheckbox;
  #recipientFieldsContainer;
  #recipientOtherProperties;
  #recipientSendOnProperty;
  constructor() {
    super(...arguments);
    this.#recipientOtherProperties = [];
  }
  connectedCallback() {
    const properties = Array.from(this.querySelectorAll('[name*="properties"]')), checkboxPropertyName = "properties[__shopify_send_gift_card_to_recipient]";
    this.#recipientCheckbox = properties.find((input) => input.name === checkboxPropertyName);
    this.#recipientOtherProperties = properties.filter((input) => input.name !== checkboxPropertyName);
    this.#recipientFieldsContainer = this.querySelector(".gift-card-recipient__fields");
    this.#offsetProperty = this.querySelector('[name="properties[__shopify_offset]"]');
    if (this.#offsetProperty) {
      this.#offsetProperty.value = (new Date()).getTimezoneOffset().toString();
    }
    this.#recipientSendOnProperty = this.querySelector('[name="properties[Send on]"]');
    const minDate = new Date();
    const maxDate = new Date();
    maxDate.setDate(minDate.getDate() + 90);
    this.#recipientSendOnProperty?.setAttribute("min", this.#formatDate(minDate));
    this.#recipientSendOnProperty?.setAttribute("max", this.#formatDate(maxDate));
    this.#recipientCheckbox?.addEventListener("change", this.#synchronizeProperties.bind(this));
    this.#synchronizeProperties();
  }
  #synchronizeProperties() {
    this.#recipientOtherProperties.forEach((property) => property.disabled = !this.#recipientCheckbox.checked);
    this.#recipientFieldsContainer.toggleAttribute("hidden", !this.#recipientCheckbox.checked);
  }
  #formatDate(date) {
    const offset = date.getTimezoneOffset();
    const offsetDate = new Date(date.getTime() - offset * 60 * 1e3);
    return offsetDate.toISOString().split("T")[0];
  }
};
if (!window.customElements.get("gift-card-recipient")) {
  window.customElements.define("gift-card-recipient", GiftCardRecipient);
}
/* ─── product-card ──────────────────────────────────────────────────── */
var ProductCard = class extends HTMLElement {
  #delegate;
  constructor() {
    super(...arguments);
    this.#delegate = new Delegate(this);
  }
  connectedCallback() {
    this.#delegate.on("change", '.product-card__info [type="radio"]', this.#onSwatchChanged.bind(this));
    this.#delegate.on("pointerover", '.product-card__info [type="radio"] + label', this.#onSwatchHovered.bind(this), true);
  }
  disconnectedCallback() {
    this.#delegate.off();
  }
  async #onSwatchHovered(event, target) {
    const control = target.control;
    const primaryMediaElement = this.querySelector(".ll-product-card__image--primary");
    if (control.hasAttribute("data-variant-media")) {
      this.#createMediaImg(JSON.parse(control.getAttribute("data-variant-media")), primaryMediaElement.className, primaryMediaElement.sizes);
    }
  }
  async #onSwatchChanged(event, target) {
    if (target.hasAttribute("data-product-url")) {
      this.querySelectorAll(`a[href^="${Shopify.routes.root}products/"`).forEach((link) => {
        link.href = target.getAttribute("data-product-url");
      });
    } else if (target.hasAttribute("data-variant-id")) {
      this.querySelectorAll(`a[href^="${Shopify.routes.root}products/"`).forEach((link) => {
        const url = new URL(link.href);
        url.searchParams.set("variant", target.getAttribute("data-variant-id"));
        link.href = `${url.pathname}${url.search}${url.hash}`;
      });
    }
    if (!target.hasAttribute("data-variant-media")) {
      return;
    }
    let newMedia = JSON.parse(target.getAttribute("data-variant-media")), primaryMediaElement = this.querySelector(".ll-product-card__image--primary"), secondaryMediaElement = this.querySelector(".ll-product-card__image--secondary"), newPrimaryMediaElement = this.#createMediaImg(newMedia, primaryMediaElement.className, primaryMediaElement.sizes), newSecondaryMediaElement = null;
    if (secondaryMediaElement && target.hasAttribute("data-variant-secondary-media")) {
      let newSecondaryMedia = JSON.parse(target.getAttribute("data-variant-secondary-media"));
      newSecondaryMediaElement = this.#createMediaImg(newSecondaryMedia, secondaryMediaElement.className, secondaryMediaElement.sizes);
      newSecondaryMediaElement.ariaHidden = "true";
    }
    if (primaryMediaElement.src !== newPrimaryMediaElement.src) {
      if (secondaryMediaElement && newSecondaryMediaElement) {
        secondaryMediaElement.replaceWith(newSecondaryMediaElement);
      }
      await primaryMediaElement.animate({ opacity: [1, 0] }, { duration: 150, easing: "ease-in", fill: "forwards" }).finished;
      await new Promise((resolve) => newPrimaryMediaElement.complete ? resolve() : newPrimaryMediaElement.onload = () => resolve());
      primaryMediaElement.replaceWith(newPrimaryMediaElement);
      newPrimaryMediaElement.animate({ opacity: [0, 1] }, { duration: 150, easing: "ease-in" });
    }
  }
  #createMediaImg(media, className, sizes) {
    return createMediaImg(media, [200, 300, 400, 500, 600, 700, 800, 1e3, 1200, 1400, 1600, 1800], { class: className, sizes });
  }
};
if (!window.customElements.get("product-card")) {
  window.customElements.define("product-card", ProductCard);
}
/* ─── product-form ──────────────────────────────────────────────────── */
var ProductForm = class extends HTMLElement {
  #abortController;
  constructor() {
    super(...arguments);
  }
  connectedCallback() {
    this.#abortController = new AbortController();
    if (this.#form) {
      this.#form.addEventListener("submit", this.#onSubmit.bind(this), { signal: this.#abortController.signal });
      this.#form.id.disabled = false;
    }
  }
  disconnectedCallback() {
    this.#abortController.abort();
  }
  get #form() {
    return this.querySelector('form[action*="/cart/add"]');
  }
  async #onSubmit(event) {
    event.preventDefault();
    if (event.submitter?.getAttribute("aria-busy") === "true") {
      return;
    }
    if (!this.#form.checkValidity()) {
      this.#form.reportValidity();
      return;
    }
    const submitButtons = Array.from(this.#form.elements).filter((button) => button.type === "submit");
    submitButtons.forEach((submitButton) => {
      submitButton.setAttribute("aria-busy", "true");
    });
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));
    const formData = new FormData(this.#form);
    formData.set("sections", sectionsToBundle.join(","));
    formData.set("sections_url", `${Shopify.routes.root}variants/${this.#form.id.value}`);
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      body: formData,
      method: "POST",
      headers: {
        "X-Requested-With": "XMLHttpRequest"
      }
    });
    submitButtons.forEach((submitButton) => {
      submitButton.removeAttribute("aria-busy");
    });
    const responseJson = await response.json();
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    if (response.ok) {
      if (window.themeVariables.settings.cartType === "page" || window.themeVariables.settings.pageType === "cart") {
        return window.location.href = `${Shopify.routes.root}cart`;
      }
      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent["sections"] = responseJson["sections"];
      this.#form.dispatchEvent(new CustomEvent("variant:add", {
        bubbles: true,
        detail: {
          items: responseJson.hasOwnProperty("items") ? responseJson["items"] : [responseJson],
          cart: cartContent,
          onSuccessDo: formData.get("on_success")
        }
      }));
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "variant:add",
          onSuccessDo: formData.get("on_success"),
          cart: cartContent
        }
      }));
    } else {
      this.#form.dispatchEvent(new CustomEvent("cart:error", {
        bubbles: true,
        detail: {
          error: responseJson["description"]
        }
      }));
      document.dispatchEvent(new CustomEvent("cart:refresh"));
    }
  }
};
if (!window.customElements.get("product-form")) {
  window.customElements.define("product-form", ProductForm);
}
/* ─── product-form-listeners ────────────────────────────────────────── */
var BuyButtons = class extends HTMLElement {
  #abortController;
  constructor() {
    super(...arguments);
  }
  connectedCallback() {
    this.#abortController = new AbortController();
    document.forms[this.getAttribute("form")]?.addEventListener("cart:error", this.#onCartError.bind(this), { signal: this.#abortController.signal });
    document.forms[this.getAttribute("form")]?.addEventListener("variant:add", this.#onVariantAdded.bind(this), { signal: this.#abortController.signal });
  }
  disconnectedCallback() {
    this.#abortController.abort();
  }
  #onVariantAdded(event) {
    if (window.themeVariables.settings.cartType === "message") {
      const bannerElement = document.createRange().createContextualFragment(`
          <div class="ll-banner banner--success" role="status" aria-live="polite">
            ${window.themeVariables.strings.addedToCart}
          </div>
        `).firstElementChild;
      this.prepend(bannerElement);
      setTimeout(() => {
        bannerElement.remove();
      }, 2500);
    } else if (window.themeVariables.settings.cartType === "drawer") {
      const items = event.detail.items;
      let content = [];
      items.forEach((item) => {
        content.push(window.themeVariables.strings.addedToCartWithTitle.replace("{{ product_title }}", item?.title || ""));
      });
      announceStatus(content.join(", "));
    }
  }
  #onCartError(event) {
    const bannerElement = document.createRange().createContextualFragment(`
        <div class="ll-banner banner--error" role="alert" aria-live="polite">
          ${event.detail.error}
        </div>
      `).firstElementChild;
    this.prepend(bannerElement);
    setTimeout(() => {
      bannerElement.remove();
    }, 2500);
  }
};
if (!window.customElements.get("buy-buttons")) {
  window.customElements.define("buy-buttons", BuyButtons);
}
/* ─── product-gallery ───────────────────────────────────────────────── */
var ProductGallery = class extends HTMLElement {
  #abortController;
  #onGestureChangedListener;
  #photoSwipeInstance;
  #settledMedia;
  constructor() {
    super();
    this.#onGestureChangedListener = this.#onGestureChanged.bind(this);
    this.addEventListener("lightbox:open", (event) => this.openLightBox(event?.detail?.index));
  }
  connectedCallback() {
    this.#abortController = new AbortController();
    if (!this.carousel) {
      return;
    }
    const form = document.forms[this.getAttribute("form")];
    if (!form) return;
    form.addEventListener("product:rerender", this.#onSectionRerender.bind(this), { signal: this.#abortController.signal });
    form.addEventListener("variant:change", this.#onVariantChange.bind(this), { signal: this.#abortController.signal });
    this.carousel.addEventListener("carousel:change", this.#onMediaChange.bind(this), { signal: this.#abortController.signal });
    this.carousel.addEventListener("carousel:settle", this.#onMediaSettle.bind(this), { signal: this.#abortController.signal });
    this.carousel.addEventListener("click", this.#onCarouselClick.bind(this), { signal: this.#abortController.signal });
    if (this.hasAttribute("allow-zoom")) {
      this.carousel.addEventListener("gesturestart", this.#onGestureStart.bind(this), { capture: false, signal: this.#abortController.signal });
    }
    this.#onMediaChange();
    this._progressBar = this.querySelector(".product-gallery__progress-bar");
    this._progressTrack = this.querySelector(".product-gallery__progress");
    if (this._progressBar) {
      this.carousel.addEventListener("carousel:change", (event) => {
        this._progressBar.style.transform = `translateY(${event.detail.index * 100}%)`;
      }, { signal: this.#abortController.signal });
      this.carousel.addEventListener("carousel:filter", () => {
        const visibleCount = this.carousel.cells.length;
        this._progressBar.style.setProperty("--slide-count", visibleCount);
        if (this._progressTrack) {
          this._progressTrack.style.display = visibleCount <= 1 ? "none" : "";
        }
      }, { signal: this.#abortController.signal });
    }
  }
  disconnectedCallback() {
    this.#abortController.abort();
  }
  get viewInSpaceButton() {
    return this.querySelector("[data-shopify-xr]");
  }
  get carousel() {
    return this.querySelector(".ll-product-gallery__carousel");
  }
  get lightBox() {
    if (this.#photoSwipeInstance) {
      return this.#photoSwipeInstance;
    }
    this.#photoSwipeInstance = new PhotoSwipeLightbox({
      pswpModule: () => import("photoswipe"),
      bgOpacity: 1,
      padding: { top: 0, bottom: 0, left: 0, right: 0 },
      maxZoomLevel: parseInt(this.getAttribute("allow-zoom")) || 3,
      closeTitle: window.themeVariables.strings.closeGallery,
      zoomTitle: window.themeVariables.strings.zoomGallery,
      errorMsg: window.themeVariables.strings.errorGallery,
      arrowPrev: false,
      arrowNext: false,
      counter: true,
      close: false,
      zoom: false
    });
    const lightboxMode = this.getAttribute("lightbox-mode") || "cover";
    this.#photoSwipeInstance.on("openingAnimationStart", () => {
      if (lightboxMode === "cover") {
        this.#photoSwipeInstance.pswp.element.classList.add("pswp--cover");
      }
    });
    this.#photoSwipeInstance.on("uiRegister", this.#registerLightboxUi.bind(this));
    this.#photoSwipeInstance.addFilter("thumbEl", (thumbEl, data) => data.thumbnailElement);
    this.#photoSwipeInstance.init();
    return this.#photoSwipeInstance;
  }
  get filteredIndexes() {
    return JSON.parse(this.getAttribute("filtered-indexes")).map((index) => parseInt(index) - 1);
  }
  openLightBox(index) {
    const images = this.carousel.cells.flatMap((cell) => Array.from(cell.querySelectorAll(":scope > img")));
    const dataSource = images.map((image) => {
      return {
        thumbnailElement: image,
        src: image.src,
        srcset: image.srcset,
        msrc: image.currentSrc || image.src,
        width: parseInt(image.getAttribute("width")),
        height: parseInt(image.getAttribute("height")),
        alt: image.alt,
        thumbCropped: true
      };
    });
    const imageCells = this.carousel.cells.filter((cell) => cell.getAttribute("data-media-type") === "image");
    this.lightBox.loadAndOpen(index ?? imageCells.indexOf(this.carousel.selectedCell), dataSource);
  }
  #registerLightboxUi() {
    this.#photoSwipeInstance.pswp.ui.registerElement({
      name: "close-button",
      className: "pswp__close-button",
      appendTo: "root",
      ariaLabel: window.themeVariables.strings.closeGallery,
      isButton: true,
      html: `
          <svg aria-hidden="true" focusable="false" fill="none" width="16" class="ll-icon" viewBox="0 0 16 16">
            <path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1"/>
          </svg>
        `,
      onClick: () => {
        this.#photoSwipeInstance.pswp.close();
      }
    });
    if (this.#photoSwipeInstance.pswp.options.dataSource.length > 1) {
      this.#photoSwipeInstance.pswp.ui.registerElement({
        name: "previous-button",
        className: "ll-circle-button hover:animate-icon-inline",
        ariaLabel: window.themeVariables.strings.previous,
        order: 1,
        isButton: true,
        html: `
          <svg aria-hidden="true" focusable="false" fill="none" width="16" class="ll-icon icon--direction-aware" viewBox="0 0 16 18">
            <path d="M11 1 3 9l8 8" stroke="currentColor" stroke-linecap="square"/>
          </svg>
        `,
        onClick: () => {
          this.#photoSwipeInstance.pswp.prev();
        }
      });
      this.#photoSwipeInstance.pswp.ui.registerElement({
        name: "next-button",
        className: "ll-circle-button hover:animate-icon-inline",
        ariaLabel: window.themeVariables.strings.next,
        order: 3,
        isButton: true,
        html: `
          <svg aria-hidden="true" focusable="false" fill="none" width="16" class="ll-icon icon--direction-aware" viewBox="0 0 16 18">
            <path d="m5 17 8-8-8-8" stroke="currentColor" stroke-linecap="square"/>
          </svg>
        `,
        onClick: () => {
          this.#photoSwipeInstance.pswp.next();
        }
      });
    }
  }
  #onSectionRerender(event) {
    const galleryMarkup = deepQuerySelector(event.detail.htmlFragment, `${this.tagName}[form="${this.getAttribute("form")}"]`);
    if (!galleryMarkup) {
      return;
    }
    if (galleryMarkup.filteredIndex !== this.filteredIndexes) {
      this.carousel.filter(galleryMarkup.filteredIndexes);
      this.setAttribute("filtered-indexes", galleryMarkup.getAttribute("filtered-indexes"));
    }
  }
  #onVariantChange(event) {
    if (!event.detail.variant) {
      return;
    }
    if (event.detail.variant["featured_media"] && event.detail.previousVariant?.["featured_media"]?.["id"] !== event.detail.variant["featured_media"]["id"]) {
      const position = event.detail.variant["featured_media"]["position"] - 1, filteredIndexBelowPosition = this.filteredIndexes.filter((filteredIndex) => filteredIndex < position);
      if (this.carousel.isScrollable) {
        this.carousel.select(position - filteredIndexBelowPosition.length, { instant: true });
      } else {
        this.querySelector(`[data-media-id="${event.detail.variant["featured_media"]["id"]}"]`)?.scrollIntoView({ block: "start", behavior: "smooth" });
      }
    }
  }
  #onMediaChange() {
    if (!this.#settledMedia) {
      return;
    }
    switch (this.#settledMedia.getAttribute("data-media-type")) {
      case "external_video":
      case "video":
      case "model":
        this.#settledMedia.firstElementChild.pause();
    }
  }
  #onMediaSettle(event) {
    const media = event ? event.detail.cell : this.carousel.selectedCell, zoomButton = this.querySelector(".ll-product-gallery__zoom-button");
    switch (media.getAttribute("data-media-type")) {
      case "image":
        this.viewInSpaceButton?.setAttribute("data-shopify-model3d-id", this.viewInSpaceButton?.getAttribute("data-shopify-model3d-default-id"));
        zoomButton?.classList.remove("ll-product-gallery__zoom-button--hidden");
        break;
      case "external_video":
      case "video":
        this.viewInSpaceButton?.setAttribute("data-shopify-model3d-id", this.viewInSpaceButton?.getAttribute("data-shopify-model3d-default-id"));
        zoomButton?.classList.add("ll-product-gallery__zoom-button--hidden");
        if (this.hasAttribute("autoplay-media")) {
          media.firstElementChild.play();
        }
        break;
      case "model":
        if (matchesMediaQuery("md")) {
          media.firstElementChild.play();
        }
        this.viewInSpaceButton?.setAttribute("data-shopify-model3d-id", event.detail.cell.getAttribute("data-media-id"));
        zoomButton?.classList.add("ll-product-gallery__zoom-button--hidden");
        break;
    }
    this.#settledMedia = media;
  }
  #onCarouselClick(event) {
    // Tikken op de foto opent de lightbox op elk formaat. Van huis uit gold dit
    // alleen vanaf md, omdat mobiel een apart zoomknopje had; dat knopje is in
    // het ontwerp weggehaald (theme.css verbergt .ll-product-gallery__zoom-button),
    // waardoor de galerij op mobiel helemaal niet meer te openen was.
    if (!this.hasAttribute("allow-zoom") || event.target.tagName !== "IMG") {
      return;
    }
    const media = event.target.closest(".ll-product-gallery__media");
    if (media.getAttribute("data-media-type") !== "image") {
      return;
    }
    const imageCells = this.carousel.cells.filter((cell) => cell.getAttribute("data-media-type") === "image");
    this.dispatchEvent(new CustomEvent("lightbox:open", { bubbles: true, detail: { index: imageCells.indexOf(media) } }));
  }
  #onGestureStart(event) {
    event.preventDefault();
    this.carousel.addEventListener("gesturechange", this.#onGestureChangedListener, { capture: false, signal: this.#abortController.signal });
  }
  #onGestureChanged(event) {
    event.preventDefault();
    if (event.scale > 1.5) {
      this.dispatchEvent(new CustomEvent("lightbox:open", { bubbles: true, detail: { index: this.carousel.selectedIndex } }));
      this.removeEventListener("gesturechange", this.#onGestureChangedListener);
    }
  }
};
var OpenLightBoxButton = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", () => this.dispatchEvent(new CustomEvent("lightbox:open", { bubbles: true })));
  }
};
if (!window.customElements.get("product-gallery")) {
  window.customElements.define("product-gallery", ProductGallery);
}
if (!window.customElements.get("open-lightbox-button")) {
  window.customElements.define("open-lightbox-button", OpenLightBoxButton);
}
/* ─── product-list ──────────────────────────────────────────────────── */
var ProductList = class extends HTMLElement {
  connectedCallback() {
    if (matchesMediaQuery("motion-safe") && this.querySelectorAll('product-card[reveal-on-scroll="true"]').length > 0) {
      inView(this, this.reveal.bind(this));
    }
  }
  reveal() {
    animate(this.querySelectorAll('product-card[reveal-on-scroll="true"]'), {
      opacity: [0, 1],
      transform: ["translateY(20px)", "translateY(0)"]
    }, {
      duration: 0.2,
      easing: "ease-in-out",
      delay: stagger(0.05, { start: 0.4, easing: "ease-out" })
    });
  }
};
if (!window.customElements.get("product-list")) {
  window.customElements.define("product-list", ProductList);
}
/* ─── product-loader ────────────────────────────────────────────────── */
var loadedProducts = {};
var ProductLoader = class {
  static load(productHandle) {
    if (!productHandle) {
      return;
    }
    if (loadedProducts[productHandle]) {
      return loadedProducts[productHandle];
    }
    loadedProducts[productHandle] = new Promise(async (resolve, reject) => {
      const response = await fetch(`${Shopify.routes.root}products/${productHandle}.js`);
      if (response.ok) {
        const responseAsJson = await response.json();
        resolve(responseAsJson);
      } else {
        reject(`
          Attempted to load information for product with handle ${productHandle}, but this product is in "draft" mode. You won't be able to
          switch between variants or access to per-variant information. To fully preview this product, change temporarily its status
          to "active".
        `);
      }
    });
    return loadedProducts[productHandle];
  }
};
/* ─── product-rerender ──────────────────────────────────────────────── */
var ProductRerender = class extends HTMLElement {
  #abortController;
  constructor() {
    super(...arguments);
  }
  connectedCallback() {
    this.#abortController = new AbortController();
    if (!this.id || !this.hasAttribute("observe-form")) {
      console.warn('The <product-rerender> requires an ID to identify the element to re-render, and an "observe-form" attribute referencing to the form to monitor.');
    }
    const rerenderForm = document.forms[this.getAttribute("observe-form")];
    if (!rerenderForm) return;
    rerenderForm.addEventListener("product:rerender", this.#onRerender.bind(this), { signal: this.#abortController.signal });
  }
  disconnectedCallback() {
    this.#abortController.abort();
  }
  #onRerender(event) {
    const matchingElement = deepQuerySelector(event.detail.htmlFragment, `#${this.id}`);
    if (!matchingElement) {
      return;
    }
    const focusedElement = document.activeElement;
    if (!this.hasAttribute("allow-partial-rerender") || event.detail.productChange) {
      this.replaceWith(matchingElement);
    } else {
      const blockTypes = ["sku", "badges", "quantity-selector", "volume-pricing", "price", "payment-terms", "variant-picker", "inventory", "buy-buttons", "liquid"];
      blockTypes.forEach((blockType) => {
        this.querySelectorAll(`[data-block-type="${blockType}"]`).forEach((element) => {
          const matchingBlock = matchingElement.querySelector(`[data-block-type="${blockType}"][data-block-id="${element.getAttribute("data-block-id")}"]`);
          if (matchingBlock) {
            if (blockType === "buy-buttons") {
              element.querySelector("buy-buttons").replaceWith(matchingBlock.querySelector("buy-buttons"));
            } else if (blockType === "payment-terms") {
              element.querySelector('[name="id"]').value = matchingBlock.querySelector('[name="id"]').value;
              element.querySelector('[name="id"]').dispatchEvent(new Event("change", { bubbles: true }));
            } else if (blockType === "quantity-selector") {
              const quantitySelectorElement = element.querySelector("quantity-selector");
              if (quantitySelectorElement) {
                const existingQuantity = quantitySelectorElement.quantity;
                element.replaceWith(matchingBlock);
                matchingBlock.querySelector("quantity-selector").quantity = existingQuantity;
              }
            } else {
              element.replaceWith(matchingBlock);
            }
          }
        });
      });
    }
    if (focusedElement.id) {
      const element = document.getElementById(focusedElement.id);
      const productRerenderScope = document.getElementById(this.id);
      if (productRerenderScope.contains(element)) {
        element.focus();
      }
    }
  }
};
if (!window.customElements.get("product-rerender")) {
  window.customElements.define("product-rerender", ProductRerender);
}
/* ─── quick-buy-modal ───────────────────────────────────────────────── */
var QuickBuyModal = class extends Modal {
  constructor() {
    super();
    if (window.themeVariables.settings.cartType === "drawer") {
      document.addEventListener("variant:add", this.hide.bind(this));
    }
    this.addEventListener("dialog:after-hide", this.#onAfterHide.bind(this));
  }
  async show() {
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
    const responseContent = await (await cachedFetch(`${window.Shopify.routes.root}products/${this.getAttribute("handle")}`)).text();
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    const tempDoc = new DOMParser().parseFromString(responseContent, "text/html");
    const quickBuyContent = tempDoc.getElementById("quick-buy-content").content;
    Array.from(quickBuyContent.querySelectorAll("noscript")).forEach((noScript) => noScript.remove());
    this.replaceChildren(quickBuyContent);
    Shopify?.PaymentButton?.init();
    return super.show();
  }
  #onAfterHide() {
    this.innerHTML = "";
  }
};
if (!window.customElements.get("quick-buy-modal")) {
  window.customElements.define("quick-buy-modal", QuickBuyModal);
}
/* ─── variant-picker ────────────────────────────────────────────────── */
var CACHE_EVICTION_TIME = 1e3 * 60 * 5;
var _VariantPicker = class _VariantPicker2 extends HTMLElement {
  #delegate;
  #form;
  #intersectionObserver;
  #selectedVariant;
  static #preloadedHtml = new Map();
  constructor() {
    super(...arguments);
    this.#delegate = new Delegate(document.body);
    this.#intersectionObserver = new IntersectionObserver(this.#onIntersection.bind(this));
  }
  async connectedCallback() {
    this.#selectedVariant = JSON.parse(this.querySelector("script[data-variant]")?.textContent || "{}");
    this.#form = document.forms[this.getAttribute("form-id")];
    this.#delegate.on("change", `input[data-option-position][form="${this.getAttribute("form-id")}"]`, this.#onOptionChanged.bind(this));
    this.#delegate.on("pointerenter", `input[data-option-position][form="${this.getAttribute("form-id")}"]:not(:checked) + label`, this.#onOptionPreload.bind(this), true);
    this.#delegate.on("touchstart", `input[data-option-position][form="${this.getAttribute("form-id")}"]:not(:checked) + label`, this.#onOptionPreload.bind(this), true);
    this.#intersectionObserver.observe(this);
  }
  disconnectedCallback() {
    this.#delegate.off();
    this.#intersectionObserver.unobserve(this);
  }
  get selectedVariant() {
    return this.#selectedVariant;
  }
  get productHandle() {
    return this.getAttribute("handle");
  }
  get updateUrl() {
    return this.hasAttribute("update-url");
  }
  async selectCombination({ optionValues, productChange }) {
    const previousVariant = this.selectedVariant;
    const newContent = document.createRange().createContextualFragment(await this.#renderForCombination(optionValues));
    if (!productChange) {
      const newVariantPicker = deepQuerySelector(newContent, `${this.tagName}[form-id="${this.getAttribute("form-id")}"]`);
      const newVariant = JSON.parse(newVariantPicker.querySelector("script[data-variant]")?.textContent || "{}");
      this.#selectedVariant = newVariant;
      this.#form.id.value = this.#selectedVariant?.id;
      this.#form.id.dispatchEvent(new Event("change", { bubbles: true }));
      if (this.updateUrl && this.#selectedVariant?.id) {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set("variant", this.#selectedVariant.id);
        window.history.replaceState({ path: newUrl.toString() }, "", newUrl.toString());
      }
    }
    this.#form.dispatchEvent(new CustomEvent("product:rerender", {
      detail: {
        htmlFragment: newContent,
        productChange
      }
    }));
    if (!productChange) {
      this.#form.dispatchEvent(new CustomEvent("variant:change", {
        bubbles: true,
        detail: {
          formId: this.#form.id,
          variant: this.#selectedVariant,
          previousVariant
        }
      }));
    }
    Shopify?.PaymentButton?.init();
  }
  #getActiveOptionValues() {
    return Array.from(this.#form.elements).filter((item) => item.matches("input[data-option-position]:checked")).sort((a, b) => parseInt(a.getAttribute("data-option-position")) - parseInt(b.getAttribute("data-option-position"))).map((input) => input.value);
  }
  #getOptionValuesFromOption(input) {
    const optionValues = [input, ...Array.from(this.#form.elements).filter((item) => item.matches(`input[data-option-position]:not([name="${input.name}"]):checked`))].sort((a, b) => parseInt(a.getAttribute("data-option-position")) - parseInt(b.getAttribute("data-option-position"))).map((input2) => input2.value);
    return optionValues;
  }
  async #onOptionChanged(event) {
    if (!event.target.name.includes("option")) {
      return;
    }
    this.selectCombination({
      optionValues: this.#getActiveOptionValues(),
      productChange: event.target.hasAttribute("data-product-url")
    });
  }
  #onOptionPreload(event, target) {
    this.#renderForCombination(this.#getOptionValuesFromOption(target.control));
  }
  #onIntersection(entries) {
    const prerenderOptions = () => {
      Array.from(this.#form.elements).filter((item) => item.matches("input[data-option-position]:not(:checked)")).forEach((input) => {
        this.#renderForCombination(this.#getOptionValuesFromOption(input));
      });
    };
    if (entries[0].isIntersecting) {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(prerenderOptions, { timeout: 2e3 });
      } else {
        prerenderOptions();
      }
    }
  }
  async #renderForCombination(optionValues) {
    const optionValuesAsString = optionValues.join(",");
    const hashKey = this.#createHashKeyForHtml(optionValuesAsString);
    let productUrl = `${Shopify.routes.root}products/${this.productHandle}`;
    for (const optionValue of optionValues) {
      const inputForOptionValue = Array.from(this.#form.elements).find((item) => item.matches(`input[value="${optionValue}"]`));
      if (inputForOptionValue?.dataset.productUrl) {
        productUrl = inputForOptionValue.dataset.productUrl;
        break;
      }
    }
    if (!_VariantPicker2.#preloadedHtml.has(hashKey)) {
      const sectionQueryParam = this.getAttribute("context") === "quick_buy" ? "" : `&section_id=${this.getAttribute("section-id")}`;
      const promise = new Promise(async (resolve) => {
        resolve(await (await fetch(`${productUrl}?option_values=${optionValuesAsString}${sectionQueryParam}`)).text());
      });
      _VariantPicker2.#preloadedHtml.set(hashKey, { htmlPromise: promise, timestamp: Date.now() });
      if (_VariantPicker2.#preloadedHtml.size > 100) {
        _VariantPicker2.#preloadedHtml.delete(Array.from(_VariantPicker2.#preloadedHtml.keys())[0]);
      }
    }
    return _VariantPicker2.#preloadedHtml.get(hashKey).htmlPromise;
  }
  #createHashKeyForHtml(optionValuesAsString) {
    return `${optionValuesAsString}-${this.getAttribute("section-id")}`;
  }
};
var VariantPicker = _VariantPicker;
if (!window.customElements.get("variant-picker")) {
  window.customElements.define("variant-picker", VariantPicker);
}
export {
  ProductLoader
};
